import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone, Send, Loader2, MessageSquare, Clock, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

// Helper to get API URL - uses current origin in production
const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};
const API_URL = getApiUrl();

const ContactPage = () => {
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi');
      }

      setSubmitted(true);
      toast.success(i18n.language === 'fr' 
        ? 'Message envoyé avec succès !' 
        : 'Message sent successfully!');
    } catch (error) {
      toast.error(i18n.language === 'fr' ? 'Erreur d\'envoi' : 'Send error');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl p-8 text-center max-w-md shadow-lg"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">
            {i18n.language === 'fr' ? 'Message envoyé !' : 'Message Sent!'}
          </h2>
          <p className="text-gray-600 mb-6">
            {i18n.language === 'fr'
              ? 'Merci de nous avoir contactés. Nous vous répondrons dans les plus brefs délais.'
              : 'Thank you for contacting us. We will get back to you as soon as possible.'}
          </p>
          <Button onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', subject: '', message: '' }); }}>
            {i18n.language === 'fr' ? 'Envoyer un autre message' : 'Send Another Message'}
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold mb-4">
            {i18n.language === 'fr' ? 'Contactez-nous' : 'Contact Us'}
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {i18n.language === 'fr'
              ? 'Une question, une suggestion ou besoin d\'aide ? Notre équipe est là pour vous.'
              : 'Have a question, suggestion, or need help? Our team is here for you.'}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1 space-y-6"
          >
            {/* Email Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Email</h3>
              <a href="mailto:contact@xperience-guides.com" className="text-primary hover:underline">
                contact@xperience-guides.com
              </a>
            </div>

            {/* Response Time Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold mb-2">
                {i18n.language === 'fr' ? 'Temps de réponse' : 'Response Time'}
              </h3>
              <p className="text-gray-600">
                {i18n.language === 'fr' ? 'Sous 24-48 heures' : 'Within 24-48 hours'}
              </p>
            </div>

            {/* Location Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold mb-2">
                {i18n.language === 'fr' ? 'Siège social' : 'Headquarters'}
              </h3>
              <p className="text-gray-600">
                Fribourg, Suisse
              </p>
            </div>

            {/* Support Card */}
            <div className="bg-gradient-to-br from-primary to-orange-600 rounded-2xl p-6 text-white">
              <MessageSquare className="w-8 h-8 mb-4" />
              <h3 className="font-semibold mb-2">
                {i18n.language === 'fr' ? 'Support prioritaire' : 'Priority Support'}
              </h3>
              <p className="text-white/80 text-sm">
                {i18n.language === 'fr'
                  ? 'Les guides et voyageurs inscrits bénéficient d\'un support prioritaire via leur tableau de bord.'
                  : 'Registered guides and travelers get priority support through their dashboard.'}
              </p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-2xl p-8 shadow-sm border">
              <h2 className="text-2xl font-bold mb-6">
                {i18n.language === 'fr' ? 'Envoyez-nous un message' : 'Send us a Message'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {i18n.language === 'fr' ? 'Nom complet *' : 'Full Name *'}
                    </label>
                    <Input
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={i18n.language === 'fr' ? 'Votre nom' : 'Your name'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder={i18n.language === 'fr' ? 'votre@email.com' : 'your@email.com'}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    {i18n.language === 'fr' ? 'Sujet *' : 'Subject *'}
                  </label>
                  <Input
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder={i18n.language === 'fr' ? 'De quoi souhaitez-vous parler ?' : 'What would you like to discuss?'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    {i18n.language === 'fr' ? 'Message *' : 'Message *'}
                  </label>
                  <Textarea
                    required
                    rows={6}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder={i18n.language === 'fr' ? 'Décrivez votre demande en détail...' : 'Describe your request in detail...'}
                  />
                </div>

                <Button type="submit" size="lg" className="w-full gap-2" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {i18n.language === 'fr' ? 'Envoi en cours...' : 'Sending...'}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      {i18n.language === 'fr' ? 'Envoyer le message' : 'Send Message'}
                    </>
                  )}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
